import { AuthenticationCompleteScreen } from "@/features/auth/screens/AuthenticationCompleteScreen";
import { authQueryOptions } from "@/features/auth/api/useAuthQuery";
import { getAuthenticatedPublicLanding } from "@/features/auth/utils/getAuthenticatedPublicLanding";
import { needsPasswordSetup } from "@/features/auth/utils/inviteAccountSetup";
import { usersOrganisationsRolesQueryOptions } from "@/features/auth/api/selectUsersOrganisationsRoles";
import { selectProfileByIdQueryOptions } from "@/features/profile/api/selectProfile";
import { getAuthCopy } from "@/features/i18n/authCopy";
import { resolvePatientResumeDestination } from "@/features/onboarding/utils/getPatientResumeDestination";
import { supabaseClient } from "@/lib/supabase";
import { getMfaState } from "@/features/auth/api/mfa";
import { getMfaRouteDecision, isMfaEnforced, isPrivilegedUser } from "@/features/auth/utils/mfaRouting";
import { readStoredLocale, resolveLocale } from "@patient-watch/i18n";
import { createFileRoute, redirect } from "@tanstack/react-router";

const AuthenticationComplete = () => {
    return <AuthenticationCompleteScreen />;
};

export const Route = createFileRoute("/_auth/authentication-complete")({
    component: AuthenticationComplete,
    beforeLoad: async ({ context }) => {
        await context?.queryClient.ensureQueryData(authQueryOptions());

        const {
            data: { user },
        } = await supabaseClient.auth.getUser();

        if (user?.id) {
            const [profile, roles] = await Promise.all([
                context?.queryClient.ensureQueryData(selectProfileByIdQueryOptions(user.id)),
                context?.queryClient.ensureQueryData(usersOrganisationsRolesQueryOptions(user.id)),
            ]);
            context?.auth?.roles?.setRoles(roles ?? []);
            context?.auth?.setIsSuperUser(profile?.is_super_user ?? false);

            if (profile && needsPasswordSetup(profile)) {
                throw redirect({ to: "/change-password", search: { source: "confirm-account" } });
            }

            if (profile) {
                const isPrivileged = isPrivilegedUser({
                    isAdmin: Boolean(context.auth.roles?.isAdmin),
                    isSuperUser: profile.is_super_user,
                });
                if (isPrivileged && isMfaEnforced()) {
                    const mfa = await getMfaState();
                    const decision = getMfaRouteDecision({
                        isPrivileged,
                        currentLevel: mfa.currentLevel,
                        nextLevel: mfa.nextLevel,
                        hasVerifiedTotp: mfa.verifiedTotp.length > 0,
                    });
                    if (decision !== "continue") {
                        throw redirect({
                            to: decision === "enroll" ? "/mfa/enroll" : "/mfa/challenge",
                            search: { returnTo: "/" },
                        });
                    }
                }
            }

            const locale = resolveLocale({ storedLocale: readStoredLocale() });
            const copy = getAuthCopy(locale);

            if (profile && context.auth.roles?.isPatient) {
                const resume = await resolvePatientResumeDestination(
                    context.queryClient,
                    user.id,
                    profile,
                    context.auth.roles,
                    locale
                );
                throw redirect({
                    to: resume.to,
                    ...(resume.params != null ? { params: resume.params } : {}),
                    ...(resume.search != null ? { search: resume.search } : {}),
                });
            }

            if (profile) {
                const landing = getAuthenticatedPublicLanding(profile, context.auth.roles, copy, locale);
                if (landing.isFirstTime) {
                    throw redirect({ to: landing.to, search: landing.search });
                }
            }
        }

        return context;
    },
});
